import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, AlertCircle, Info, CheckCircle } from "lucide-react";

export type ConfirmationVariant =
  | "default"
  | "warning"
  | "error"
  | "info"
  | "success";

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  variant?: ConfirmationVariant;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading = false,
  variant = "default",
}) => {
  // Always call hooks — only do work if open
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  const variants = {
    default: { icon: Info, color: "text-blue-500", bg: "bg-blue-50" },
    warning: { icon: AlertTriangle, color: "text-yellow-500", bg: "bg-yellow-50" },
    error: { icon: AlertCircle, color: "text-red-500", bg: "bg-red-50" },
    info: { icon: Info, color: "text-blue-500", bg: "bg-blue-50" },
    success: { icon: CheckCircle, color: "text-green-500", bg: "bg-green-50" },
  };

  const Icon = variants[variant].icon;

  // If closed, don't render anything
  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md mx-4 rounded-lg shadow-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-start gap-3 p-5 border-b ${variants[variant].bg}`}>
          <Icon className={`mt-1 ${variants[variant].color}`} size={24} />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-600 mt-1">{message}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 bg-gray-50 border-t">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${
              variant === "error"
                ? "bg-red-600 hover:bg-red-700"
                : variant === "warning"
                ? "bg-yellow-500 hover:bg-yellow-600"
                : variant === "success"
                ? "bg-green-600 hover:bg-green-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Please wait..." : confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmationDialog;
